import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  FaChartLine,
  FaDollarSign,
  FaHome,
  FaSignOutAlt,
  FaUsers,
  FaUpload,
  FaBars,
  FaChevronDown,
  FaChevronRight,
  FaChartPie,
  FaTable,
  FaCog,
  FaBalanceScale,
  FaFileInvoice,
  FaMap,
  FaFileAlt,
  FaIdCard,
  FaShieldAlt,
} from "react-icons/fa";
import { FaRankingStar } from "react-icons/fa6";
import LogoImageSrc from "../assets/Logo2.png";
import "./Sidebar.css";
import { jwtDecode } from "jwt-decode";

const MOBILE_BREAKPOINT = 1220; // px

const Sidebar = () => {
  const [email, setEmail] = useState("");
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isOpen, setIsOpen] = useState(true);
  const [submenuOpen, setSubmenuOpen] = useState({
    sprzedaz: false,
    zakupy: false,
    raporty: false,
    admin: false,
    kontrahent: false,
    import: false,
  });

  const [activeMenu, setActiveMenu] = useState("");
  const [licenseType, setLicenseType] = useState("");
  const [licenseExpire, setLicenseExpire] = useState("");
  const [licenseColor, setLicenseColor] = useState("#9E9E9E");
  const [token, setToken] = useState("");

  const navigate = useNavigate();
  const location = useLocation();

  // track mobile
  const [isMobile, setIsMobile] = useState(
    window.innerWidth <= MOBILE_BREAKPOINT
  );

  const handleResize = useCallback(() => {
    const mobileNow = window.innerWidth <= MOBILE_BREAKPOINT;
    setIsMobile(mobileNow);
    // Auto-close drawer on enter mobile; open on desktop
    setIsOpen(mobileNow ? false : true);
  }, []);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    handleResize(); // initial
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  // Scroll-lock when drawer open on mobile
  useEffect(() => {
    if (isMobile && isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobile, isOpen]);

  useEffect(() => {
    const savedToken = localStorage.getItem("token");
    if (savedToken) setToken(savedToken);
  }, []);

  // keep CSS var in sync on desktop only (mobile uses transform)
  useEffect(() => {
    if (!isMobile) {
      const w = isOpen ? "250px" : "70px";
      document.documentElement.style.setProperty("--sb-width", w);
    }
  }, [isOpen, isMobile]);

  useEffect(() => {
    // initial width var for desktop
    if (!isMobile) {
      const w = isOpen ? "250px" : "70px";
      document.documentElement.style.setProperty("--sb-width", w);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const licenseOptions = {
    DEMO: [
      {
        label: "DEMO BASIC - 7 dni",
        value: "DEMO BASIC - 7 dni",
        days: 7,
        color: "#FFC107",
      },
      {
        label: "DEMO EXTENDED - 15 dni",
        value: "DEMO EXTENDED - 15 dni",
        days: 15,
        color: "#FF9800",
      },
    ],
    STANDARD: [
      {
        label: "1 miesiąc",
        value: "STANDARD - 1 miesiąc",
        months: 1,
        color: "#2196F3",
      },
      {
        label: "6 miesięcy",
        value: "STANDARD - 6 miesięcy",
        months: 6,
        color: "#1976D2",
      },
      {
        label: "1 rok",
        value: "STANDARD - 1 rok",
        years: 1,
        color: "#0D47A1",
      },
    ],
    PRO: [
      {
        label: "1 miesiąc",
        value: "PRO - 1 miesiąc",
        months: 1,
        color: "#4CAF50",
      },
      {
        label: "6 miesięcy",
        value: "PRO - 6 miesięcy",
        months: 6,
        color: "#388E3C",
      },
      { label: "1 rok", value: "PRO - 1 rok", years: 1, color: "#1B5E20" },
    ],
    ENTERPRISE: [
      {
        label: "1 miesiąc",
        value: "ENTERPRISE - 1 miesiąc",
        months: 1,
        color: "#9C27B0",
      },
      {
        label: "6 miesięcy",
        value: "ENTERPRISE - 6 miesięcy",
        months: 6,
        color: "#7B1FA2",
      },
      {
        label: "1 rok",
        value: "ENTERPRISE - 1 rok",
        years: 1,
        color: "#4A148C",
      },
    ],
    NO_EXPIRE: [
      {
        label: "Bez końca (99 lat)",
        value: "NO EXPIRE",
        years: 99,
        color: "#FF5722",
      },
    ],
  };

  const getLicenseColor = (lt) => {
    for (const category in licenseOptions) {
      const found = licenseOptions[category].find(
        (option) => option.value === lt
      );
      if (found) return found.color;
    }
    return "#9E9E9E";
  };

  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    if (storedToken) {
      try {
        const decoded = jwtDecode(storedToken);
        setLicenseType(decoded.LicenseType || "Brak informacji");
        setLicenseColor(getLicenseColor(decoded.LicenseType));
        if (decoded.LicenseExpire && decoded.LicenseExpire !== "NO EXPIRE") {
          const formattedDate = new Date(decoded.LicenseExpire).toLocaleDateString(
            "pl-PL",
            {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            }
          );
          setLicenseExpire(formattedDate);
        } else {
          setLicenseExpire("Bezterminowa");
        }
      } catch (e) {
        console.error("Error decoding JWT:", e);
      }
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const path = location.pathname;
    const isKontrahentRaport = path === "/kontrahent/raport-wiarygodnosci";

    setSubmenuOpen((prev) => ({
      ...prev,
      sprzedaz: path.startsWith("/sprzedaz") && path !== "/sprzedaz/dashboard",
      zakupy: path.startsWith("/zakupy"),
      raporty: path.startsWith("/raporty"),
      admin: path.startsWith("/admin"),
      kontrahent:
        (!isKontrahentRaport && path.startsWith("/kontrahent")) ||
        path === "/klienci",
      import: path === "/import" || path.startsWith("/import/"),
    }));

    if (path.startsWith("/sprzedaz") && path !== "/sprzedaz/dashboard") {
      setActiveMenu("sprzedaz");
    } else if (path.startsWith("/zakupy")) {
      setActiveMenu("zakupy");
    } else if (path.startsWith("/raporty")) {
      setActiveMenu("raporty");
    } else if (path.startsWith("/admin")) {
      setActiveMenu("admin");
    } else if (
      (!isKontrahentRaport && path.startsWith("/kontrahent")) ||
      path === "/klienci"
    ) {
      setActiveMenu("kontrahent");
    } else if (path === "/import" || path.startsWith("/import/")) {
      setActiveMenu("import");
    } else {
      setActiveMenu("");
    }

    if (!isMobile) setIsOpen(true);
  }, [location.pathname, isMobile]);

  const formatDateTime = (date) =>
    date.toLocaleDateString("en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });

  const handleLogout = async () => {
    localStorage.clear();
    if (window.indexedDB?.databases) {
      const dbs = await window.indexedDB.databases();
      dbs.forEach((dbInfo) => window.indexedDB.deleteDatabase(dbInfo.name));
    }
    navigate("/login");
  };

  // CLICK MAIN MENU: toggle its submenu (click again = hide)
  const handleMenuClick = (key) => {
    setSubmenuOpen((prev) => {
      const isCurrentlyOpen = prev[key];
      const next = Object.keys(prev).reduce((acc, submenu) => {
        if (submenu === key) {
          acc[submenu] = !isCurrentlyOpen; // toggle clicked one
        } else {
          acc[submenu] = false; // close all others
        }
        return acc;
      }, {});
      return next;
    });

    setActiveMenu((prevActive) => (prevActive === key ? "" : key));
    setIsOpen(true);
  };

  const closeOnMobile = () => {
    if (isMobile) setIsOpen(false);
  };

  // Floating toggle – visible on mobile
  const FloatingToggle = () => (
    <button
      className={`toggle-button-floating ${isOpen ? "open" : ""}`}
      aria-label="Toggle sidebar"
      aria-expanded={isOpen}
      aria-controls="app-sidebar"
      onClick={() => setIsOpen((o) => !o)}
    >
      <FaBars />
    </button>
  );

  const isKontrahentRaport =
    location.pathname === "/kontrahent/raport-wiarygodnosci";

  return (
    <>
      {/* Backdrop for mobile */}
      {isMobile && isOpen && (
        <div className="sb-backdrop" onClick={() => setIsOpen(false)} />
      )}

      {/* Floating burger on mobile */}
      {isMobile && <FloatingToggle />}

      <div
        id="app-sidebar"
        className={`sidebar-wrapper ${!isOpen ? "closed" : ""} ${
          isMobile ? "mobile" : "desktop"
        }`}
        role="navigation"
      >
        {/* Desktop toggle stays inside */}
        {!isMobile && (
          <button className="toggle-button" onClick={() => setIsOpen(!isOpen)}>
            <FaBars />
          </button>
        )}

        <div className={`logo-container ${!isOpen && !isMobile ? "closed" : ""}`}>
          <img src={LogoImageSrc} alt="Logo" className="logo-image" />
          <div className="license-info"></div>
        </div>

        {/* Dashboard */}
        <Link
          to={`/sprzedaz/dashboard?token=${token}`}
          onClick={closeOnMobile}
          className={`menu-item ${
            location.pathname === "/sprzedaz/dashboard" ? "active" : ""
          } ${!isOpen && !isMobile ? "closed" : ""}`}
        >
          <FaHome />
          <span className={`menu-item-text ${!isOpen && !isMobile ? "closed" : ""}`}>
            Dashboard
          </span>
        </Link>

        {/* Raport o nas */}
        <Link
          to={`/raportonas?token=${token}`}
          onClick={closeOnMobile}
          className={`menu-item ${
            location.pathname === "/raportonas" ? "active" : ""
          } ${!isOpen && !isMobile ? "closed" : ""}`}
        >
          <FaFileAlt />
          <span className={`menu-item-text ${!isOpen && !isMobile ? "closed" : ""}`}>
            Raport o nas
          </span>
        </Link>

        {/* Raport wiarygodności – main menu */}
        <Link
          to={`/kontrahent/raport-wiarygodnosci?token=${token}`}
          onClick={closeOnMobile}
          className={`menu-item ${
            location.pathname === "/kontrahent/raport-wiarygodnosci"
              ? "active"
              : ""
          } ${!isOpen && !isMobile ? "closed" : ""}`}
        >
          <FaShieldAlt />
          <span className={`menu-item-text ${!isOpen && !isMobile ? "closed" : ""}`}>
            Raport wiarygodności
          </span>
        </Link>

        {/* Sprzedaż */}
        <div
          className={`menu-item has-submenu ${
            activeMenu === "sprzedaz"
              ? "new-active"
              : submenuOpen.sprzedaz ||
                (location.pathname.startsWith("/sprzedaz") &&
                  location.pathname !== "/sprzedaz/dashboard")
              ? "active"
              : ""
          }`}
          onClick={() => handleMenuClick("sprzedaz")}
        >
          <div>
            <FaChartLine />
            <span
              className={`menu-item-text ${
                !isOpen && !isMobile ? "closed" : ""
              }`}
            >
              Sprzedaż
            </span>
          </div>
          {(isOpen || isMobile) && (
            <span>{submenuOpen.sprzedaz ? <FaChevronDown /> : <FaChevronRight />}</span>
          )}
        </div>

        {(isOpen || isMobile) && submenuOpen.sprzedaz && (
          <div className="submenu open">
            <Link
              to={`/sprzedaz/daily?token=${token}`}
              onClick={closeOnMobile}
              className={`submenu-item ${
                location.pathname === "/sprzedaz/daily" ? "active" : ""
              }`}
            >
              <FaChartLine /> Sprzedaż wg okresów
            </Link>
            <Link
              to={`/sprzedaz/kontrahent?token=${token}`}
              onClick={closeOnMobile}
              className={`submenu-item ${
                location.pathname === "/sprzedaz/kontrahent" ? "active" : ""
              }`}
            >
              <FaUsers /> Sprzedaż wg kontrahenta
            </Link>
            <Link
              to={`/sprzedaz/region?token=${token}`}
              onClick={closeOnMobile}
              className={`submenu-item ${
                location.pathname === "/sprzedaz/region" ? "active" : ""
              }`}
            >
              <FaMap /> Sprzedaż wg regionów
            </Link>
            <Link
              to={`/sprzedaz/typow?token=${token}`}
              onClick={closeOnMobile}
              className={`submenu-item ${
                location.pathname === "/sprzedaz/typow" ? "active" : ""
              }`}
            >
              <FaChartPie /> Sprzedaż wg typów
            </Link>
            <Link
              to={`/sprzedaz/compare?token=${token}`}
              onClick={closeOnMobile}
              className={`submenu-item ${
                location.pathname === "/sprzedaz/compare" ? "active" : ""
              }`}
            >
              <FaBalanceScale /> Porównanie sprzedaży
            </Link>
            <Link
              to={`/sprzedaz/creative?token=${token}`}
              onClick={closeOnMobile}
              className={`submenu-item ${
                location.pathname === "/sprzedaz/creative" ? "active" : ""
              }`}
            >
              <FaRankingStar /> Raporty Kreatywne
            </Link>
            <Link
              to={`/sprzedaz/dane?token=${token}`}
              onClick={closeOnMobile}
              className={`submenu-item ${
                location.pathname === "/sprzedaz/dane" ? "active" : ""
              }`}
            >
              <FaTable /> Sprzedaż Dane
            </Link>
          </div>
        )}

        {/* Zakupy */}
        <div
          className={`menu-item has-submenu ${
            activeMenu === "zakupy"
              ? "new-active"
              : submenuOpen.zakupy || location.pathname.startsWith("/zakupy")
              ? "active"
              : ""
          }`}
          onClick={() => handleMenuClick("zakupy")}
        >
          <div>
            <FaDollarSign />
            <span
              className={`menu-item-text ${
                !isOpen && !isMobile ? "closed" : ""
              }`}
            >
              Zakupy
            </span>
          </div>
          {(isOpen || isMobile) && (
            <span>{submenuOpen.zakupy ? <FaChevronDown /> : <FaChevronRight />}</span>
          )}
        </div>

        {(isOpen || isMobile) && submenuOpen.zakupy && (
          <div className="submenu open">
            <Link
              to={`/zakupy/daily?token=${token}`}
              onClick={closeOnMobile}
              className={`submenu-item ${
                location.pathname === "/zakupy/daily" ? "active" : ""
              }`}
            >
              <FaChartLine /> Zakupy wg okresów
            </Link>
            <Link
              to={`/zakupy/kontrahent?token=${token}`}
              onClick={closeOnMobile}
              className={`submenu-item ${
                location.pathname === "/zakupy/kontrahent" ? "active" : ""
              }`}
            >
              <FaUsers /> Zakupy wg kontrahenta
            </Link>
            <Link
              to={`/zakupy/region?token=${token}`}
              onClick={closeOnMobile}
              className={`submenu-item ${
                location.pathname === "/zakupy/region" ? "active" : ""
              }`}
            >
              <FaMap /> Zakupy wg regionów
            </Link>
            <Link
              to={`/zakupy/typow?token=${token}`}
              onClick={closeOnMobile}
              className={`submenu-item ${
                location.pathname === "/zakupy/typow" ? "active" : ""
              }`}
            >
              <FaChartPie /> Zakupy wg typów
            </Link>
            <Link
              to={`/zakupy/compare?token=${token}`}
              onClick={closeOnMobile}
              className={`submenu-item ${
                location.pathname === "/zakupy/compare" ? "active" : ""
              }`}
            >
              <FaBalanceScale /> Porównanie zakupów
            </Link>
            <Link
              to={`/zakupy/creative?token=${token}`}
              onClick={closeOnMobile}
              className={`submenu-item ${
                location.pathname === "/zakupy/creative" ? "active" : ""
              }`}
            >
              <FaRankingStar /> Raporty Kreatywne
            </Link>
            <Link
              to={`/zakupy/dane?token=${token}`}
              onClick={closeOnMobile}
              className={`submenu-item ${
                location.pathname === "/zakupy/dane" ? "active" : ""
              }`}
            >
              <FaTable /> Zakupy Dane
            </Link>
          </div>
        )}

        {/* Kontrahent */}
        <div
          className={`menu-item has-submenu ${
            activeMenu === "kontrahent"
              ? "new-active"
              : submenuOpen.kontrahent ||
                (!isKontrahentRaport &&
                  location.pathname.startsWith("/kontrahent")) ||
                location.pathname === "/klienci"
              ? "active"
              : ""
          }`}
          onClick={() => handleMenuClick("kontrahent")}
        >
          <div>
            <FaUsers />
            <span
              className={`menu-item-text ${
                !isOpen && !isMobile ? "closed" : ""
              }`}
            >
              Kontrahent
            </span>
          </div>
          {(isOpen || isMobile) && (
            <span>
              {submenuOpen.kontrahent ? <FaChevronDown /> : <FaChevronRight />}
            </span>
          )}
        </div>

        {(isOpen || isMobile) && submenuOpen.kontrahent && (
          <div className="submenu open">
            <Link
              to={`/klienci?token=${token}`}
              onClick={closeOnMobile}
              className={`submenu-item ${
                location.pathname === "/klienci" ? "active" : ""
              }`}
            >
              <FaUsers /> Moi kontrahenci
            </Link>

            <Link
              to={`/kontrahent/krs-pelny?token=${token}`}
              onClick={closeOnMobile}
              className={`submenu-item ${
                location.pathname === "/kontrahent/krs-pelny" ? "active" : ""
              }`}
            >
              <FaFileAlt /> Odpis KRS Pełny
            </Link>

            <Link
              to={`/kontrahent/krs-aktualny?token=${token}`}
              onClick={closeOnMobile}
              className={`submenu-item ${
                location.pathname === "/kontrahent/krs-aktualny"
                  ? "active"
                  : ""
              }`}
            >
              <FaFileAlt /> Odpis KRS aktualny
            </Link>

            <Link
              to={`/kontrahent/ceodg-aktualny?token=${token}`}
              onClick={closeOnMobile}
              className={`submenu-item ${
                location.pathname === "/kontrahent/ceodg-aktualny"
                  ? "active"
                  : ""
              }`}
            >
              <FaFileInvoice /> Aktualny wpis z CEODG
            </Link>
          </div>
        )}

        {/* Raporty (existing conditional submenu render) */}
        {(isOpen || isMobile) && submenuOpen.raporty && (
          <div className="submenu open">
            <Link
              to={`/raporty/kontrahent?token=${token}`}
              onClick={closeOnMobile}
              className={`submenu-item ${
                location.pathname === "/raporty/kontrahent" ? "active" : ""
              }`}
            >
              <FaIdCard /> Kontrahent
            </Link>
          </div>
        )}

        {/* Import */}
        <div
          className={`menu-item has-submenu ${
            activeMenu === "import"
              ? "new-active"
              : submenuOpen.import ||
                location.pathname === "/import" ||
                location.pathname.startsWith("/import/")
              ? "active"
              : ""
          }`}
          onClick={() => handleMenuClick("import")}
        >
          <div>
            <FaUpload />
            <span
              className={`menu-item-text ${
                !isOpen && !isMobile ? "closed" : ""
              }`}
            >
              Import
            </span>
          </div>
          {(isOpen || isMobile) && (
            <span>{submenuOpen.import ? <FaChevronDown /> : <FaChevronRight />}</span>
          )}
        </div>

        {(isOpen || isMobile) && submenuOpen.import && (
          <div className="submenu open">
            <Link
              to={`/import?token=${token}`}
              onClick={closeOnMobile}
              className={`submenu-item ${
                location.pathname === "/import" ? "active" : ""
              }`}
            >
              <FaUpload /> Import danych
            </Link>

            <Link
              to={`/import/przeglad?token=${token}`}
              onClick={closeOnMobile}
              className={`submenu-item ${
                location.pathname === "/import/przeglad" ? "active" : ""
              }`}
            >
              <FaTable /> Przegląd importów
            </Link>

            <Link
              to={`/import/historia?token=${token}`}
              onClick={closeOnMobile}
              className={`submenu-item ${
                location.pathname === "/import/historia" ? "active" : ""
              }`}
            >
              <FaFileAlt /> Historia importów
            </Link>
          </div>
        )}

        {/* Admin */}
        <div
          className={`menu-item has-submenu ${
            activeMenu === "admin"
              ? "new-active"
              : submenuOpen.admin || location.pathname.startsWith("/admin")
              ? "active"
              : ""
          }`}
          onClick={() => handleMenuClick("admin")}
        >
          <div>
            <FaCog />
            <span
              className={`menu-item-text ${
                !isOpen && !isMobile ? "closed" : ""
              }`}
            >
              Ustawienia i Dane
            </span>
          </div>
          {(isOpen || isMobile) && (
            <span>{submenuOpen.admin ? <FaChevronDown /> : <FaChevronRight />}</span>
          )}
        </div>

        {(isOpen || isMobile) && submenuOpen.admin && (
          <div className={`submenu ${submenuOpen.admin ? "open" : ""}`}>
            <Link
              to={`/admin/faktura?token=${token}`}
              onClick={closeOnMobile}
              className={`submenu-item ${
                location.pathname === "/admin/faktura" ? "active" : ""
              }`}
            >
              <FaFileInvoice /> Faktury
            </Link>

            <Link
              to={`/admin/ustawenia?token=${token}`}
              onClick={closeOnMobile}
              className={`submenu-item ${
                location.pathname === "/admin/ustawenia" ? "active" : ""
              }`}
            >
              <FaCog /> Dane do faktury
            </Link>

            <Link
              to={`/admin/subskrypcja?token=${token}`}
              onClick={closeOnMobile}
              className={`submenu-item ${
                location.pathname === "/admin/subskrypcja" ? "active" : ""
              }`}
            >
              <FaIdCard /> Dane Subskrypcji i Firmy
            </Link>
          </div>
        )}

        <div className={`last-updated ${!isOpen && !isMobile ? "closed" : ""}`}>
          OSTATNIA AKTUALIZACJA: <br /> {formatDateTime(currentTime)}
        </div>

        <button className="logout-button" onClick={handleLogout}>
          <FaSignOutAlt />
          <span
            className={`menu-item-text ${
              !isOpen && !isMobile ? "closed" : ""
            }`}
          >
            Wyloguj
          </span>
        </button>
      </div>
    </>
  );
};

export default Sidebar;
